import React, { useState, useEffect, useRef } from "react";
import {
  InstantSearch,
  RefinementList,
  Stats,
  CurrentRefinements,
  ClearRefinements,
  Configure,
  Hits,
} from "react-instantsearch";
import DictionaryHit from "../../components/Dictionary/DictionaryHits";
import DictionaryPagination from "../../components/Dictionary/DictionaryPagination";
import SearchLayout from "../../layouts/SearchLayout";
import { searchClient } from "../../services/typesense-client";
import { Helmet } from "react-helmet-async";
import abcIcon from "../../assets/images/Alphabet_color.png";
import { FiFilter } from "react-icons/fi";
import MobileAlphabetFilter from "../../components/Header/AlphabetFilter/MobileAlphabetFilter";
import { CategoryFilterModal } from "../../components/Header/CategoryFilter";

const TrilingualDictionary = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const PlaceHolder = "Type a word, சொல், වචනය";

  const DictionaryClient = searchClient(
    "english, tamil, sinhala",
    "off",
    "123",
    "dictionary"
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const commonClassNames = {
    root: "flex flex-col justify-center",
    noRefinementRoot: "",
    noResults: "text-11 pl-3",
    item: "flex flex-col mb-1",
    selectedItem: "",
    list: "list-none w-full px-0 mb-0",
    count:
      "bg-[#9d9d9d30] rounded-full lg:text-10 font-normal lg:px-2 px-1 ml-20 dark:bg-[#434343]",
    checkbox: "ml-5 mr-2 flex-shrink-0 background-color: #df0d7d",
    label:
      "flex items-center px-10 text-12 mb-1 dark:text-pl_txt_dark_3 text-pl_txt_light_3",
    labelText: "block truncate hover:whitespace-normal",
  };

  return (
    <div className="relative">
      {/* Main Content */}
      <InstantSearch
        indexName={import.meta.env.VITE_DICTIONARY_TYPESENSE_COLLECTION_NAME}
        searchClient={DictionaryClient}
        routing={true}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        <div className="w-full flex-grow">
          <Helmet>
            <title>Trilingual Dictionary | paralegal.lk</title>
          </Helmet>

          <SearchLayout
            isResults={true}
            showTab={false}
            showAlphabet={true}
            showSearch={true}
            PlaceHolder={PlaceHolder}
            enableFiltering={false}
            enableSearchTracking={false}
            enableSuggestions={false}
            SearchAsYouType={true}
          >
            <div className="flex flex-col py-20 pt-[160px] md:pt-[120px] max-w-6xl w-full">
              <div className="px-10 mb-8 md:px-20 flex flex-nowrap w-full overflow-x-auto py-1">
                <CurrentRefinements
                  classNames={{
                    root: "flex whitespace-nowrap",
                    noRefinementRoot: "",
                    noRefinementList: "",
                    item: "",
                    label: "hidden",
                    category:
                      "mx-0.5 rounded-full py-0.5 px-2 text-13 transition-all dark:text-pl_txt_dark_1 text-pl_txt_light_1 bg-pl_bg_light_1 dark:bg-pl_bg_dark_3 border dark:border-pl_border_dark_1 border-pl_border_light_1",
                    categoryLabel: "me-1",
                    list: "flex flex-nowrap",
                  }}
                />
                <ClearRefinements
                  translations={{
                    resetButtonText: "Clear all",
                  }}
                  classNames={{
                    root: "",
                    button:
                      "whitespace-nowrap mx-0.5 rounded-full py-0.5 px-2 text-13 transition-all dark:text-pl_txt_dark_1 text-pl_txt_light_1 bg-pl_bg_light_2 dark:bg-pl_bg_dark_2 border dark:border-pl_border_dark_1 border-pl_border_light_1",
                    disabledButton: "hidden",
                  }}
                />
              </div>

              <Configure hitsPerPage={15} />

              {/* Stats display */}
              <div className="flex w-full md:gap-50 text-12 md:text-14 items-center mb-4">
                <div className="flex flex-grow w-3/4 px-6 dark:text-pl_txt_dark_3 text-pl_txt_light_3">
                  <Stats
                    translations={{
                      rootElementText(nbHits) {
                        let statsText = "";
                        if (nbHits.nbHits === 0) {
                          statsText = "No results";
                        } else if (nbHits.nbHits === 1) {
                          statsText = "1 result";
                        } else {
                          statsText = `${nbHits.nbHits} results`;
                        }
                        return `${statsText.toLocaleString()} found in ${
                          nbHits.processingTimeMS
                        } ms.`;
                      },
                    }}
                  />
                </div>
                <div className="flex justify-start items-center gap-10 mr-20 md:mr-0 sm:gap-30 sm:w-1/4">
                  {!isDesktop && (
                    <div>
                      <div className="flex items-center gap-3 ml-3 relative">
                        <button
                          onClick={() => setShowCategoryModal(true)}
                          className="flex items-center justify-center  bg-white dark:bg-pl_bg_dark_4 hover:bg-pl_prim_color_1/10 dark:hover:bg-pl_prim_color_1/20 transition-all shadow-sm"
                          aria-label="Filter by Category"
                        >
                          <FiFilter className="w-20 h-20 text-pl_prim_color_1" />
                        </button>
                        {/* New Category Filter Modal */}
                        <CategoryFilterModal
                          isOpen={showCategoryModal}
                          onClose={() => setShowCategoryModal(false)}
                        />
                        <button
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="relative"
                        >
                          <img
                            src={abcIcon}
                            alt="Alphabet_color"
                            className="w-8 h-8 "
                          />
                        </button>

                        <MobileAlphabetFilter
                          isOpen={showDropdown}
                          onClose={() => setShowDropdown(false)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Terms List */}
              <div className="flex w-full md:gap-50">
                <div className="md:w-3/4 w-full flex flex-col justify-between h-full">
                  <div className="flex-1 dark:text-pl_txt_dark_3 text-pl_txt_light_3">
                    <Hits hitComponent={DictionaryHit} />
                  </div>
                  <div className="mt-50">
                    <DictionaryPagination />
                  </div>
                </div>
                {isDesktop && (
                  <div className="flex w-1/4 mt-35 px-10">
                    <div className="pl-filter flex flex-col w-full self-start">
                      <div className="bg-pl_bg_light_1 dark:bg-pl_bg_dark_3 overflow-hidden border dark:border-pl_border_dark_1 border-pl_border_light_1 rounded-lg mt-20">
                        {/* Categories */}
                        <h2 className="py-6 px-10 cursor-pointer flex text-left text-14 md:text-14 font-medium dark:text-pl_txt_dark_3 text-pl_txt_light_4 hover:bg-pl_bg_light_1 hover:dark:bg-pl_bg_dark_4">
                          Filter by category
                        </h2>
                        <RefinementList
                          attribute="category"
                          showMore={false}
                          limit={100}
                          sortBy={["name:asc"]}
                          classNames={commonClassNames}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </SearchLayout>
        </div>
      </InstantSearch>
    </div>
  );
};

export default TrilingualDictionary;
